import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

export const useLevels = defineStore("/levels", () => {
  const allLevels = ref<Levels>([]);

  const isInitialized = ref(false);
  const subscription = ref<Unsubscribe | undefined>();
  const isLoadingLevels = ref(false);
  const hasInitializedUserLevels = ref(false);

  const initializeLevels = async () => {
    if (isInitialized.value) return;

    isLoadingLevels.value = true;

    const db = useFirestore();
    const levelsCollectionRef = collection(db, "levels");
    const levelsCollectionQuery = query(
      levelsCollectionRef,
      orderBy("displayOrder", "asc")
    );

    var levelsSnapshot = await getDocs(levelsCollectionRef);
    allLevels.value = [
      ...levelsSnapshot.docs.map((doc) => {
        return {
          ...doc.data(),
          id: doc.id,
        } as Level;
      }),
    ];

    if (subscription.value) {
      subscription.value();
    }

    subscription.value = onSnapshot(levelsCollectionQuery, (snapshot) => {
      allLevels.value = [
        ...snapshot.docs.map((doc) => {
          return {
            ...doc.data(),
            id: doc.id,
          } as Level;
        }),
      ];

      isLoadingLevels.value = false;
    });

    isInitialized.value = true;
  };

  const allUserLevels = computed(() => {
    const appUserData = useAppUserDataStore();
    const { availableLevelIds, isDBClaimsInitialized } =
      storeToRefs(appUserData);

    hasInitializedUserLevels.value =
      isDBClaimsInitialized.value && isLoadingLevels.value != true;

    // Users now only have acccess to the levels they have explicitly enabled.
    return allLevels.value.filter((level) => {
      return availableLevelIds.value.includes(level.id!);
    });
  });

  const productCategoryLevels = computed(() => {
    return (productCategoryId: string) => {
      return allLevels.value.filter((level) => {
        return level.iewGradebookProductCategoryId == productCategoryId;
      });
    };
  });

  const fetchLevel = async (levelId: string) => {
    const db = useFirestore();
    const levelRef = doc(db, "levels", levelId);
    const levelDoc = await getDoc(levelRef);

    if (levelDoc.exists()) {
      return {
        ...levelDoc.data(),
        id: levelDoc.id,
      } as Level;
    } else {
      return null;
    }
  };

  const saveLevel = async (level: Level) => {
    const db = useFirestore();
    if (level.id) {
      const levelRef = doc(db, "levels", level.id);
      await updateDoc(levelRef, level);
    } else {
      const levelsCollectionRef = collection(db, "levels");
      await addDoc(levelsCollectionRef, level);
    }
  };

  const saveLevels = async (levels: Levels) => {
    const db = useFirestore();
    const batch = writeBatch(db);

    for (var level of levels) {
      const levelRef = doc(db, "levels", level.id!);
      batch.set(levelRef, level);
    }

    await batch.commit();
  };

  return {
    allLevels,
    allUserLevels,
    productCategoryLevels,
    initializeLevels,
    fetchLevel,
    saveLevel,
    saveLevels,
    isLoadingLevels,
    isInitialized,
    hasInitializedUserLevels,
  };
});
